import React, { Suspense, useState, useEffect } from "react";
import axios from "axios";
import {
  BrowserRouter,
  useRoutes,
  Navigate,
  useLocation,
} from "react-router-dom";

import "bootstrap-icons/font/bootstrap-icons.css";
import "./App.css";

import Layout from "./components/Layout";
import ProtectedRoute from "./components/ProtectedRoute";
import { AuthProvider } from "./context/AuthContext";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import Login from "./pages/Login";
import Signup from "./pages/Signup";
import ForgotPassword from "./pages/ForgotPassword";
import Dashboard from "./pages/Dashboard";
import MailerList from "./pages/MailerList";
import TenderBiddersPage from "./pages/TenderBiddersPage";
import TenderList from "./pages/TenderList";
import TaskList from "./pages/TaskList";
import FeedBack from "./pages/FeedBack";
import Report from "./pages/Report";
import FeedBackSales from "./pages/FeedBackSales";
import OperationsReport from "./pages/OperationsReport";
import FinanceReport from "./pages/FinanceReport";
import Empcustomerassi from "./pages/Empcustomerassi";
import BillingTable from "./components/tables/BillingTable";
import AuthModal from "./components/AuthModal";
import PageSkeleton from "./components/skeletons/PageSkeleton";
import CheckList from "./pages/CheckList";
import DocumentChecklist from "./pages/DocumentChecklist";
import { TENDERS } from "./constants";
import AdminAnalytics from "./components/Adminanalytics";
import Customeranalytics from "./components/Customeranalytics";
import Financeanalytics from "./components/Financeanalytics";
import Salesanalytics from "./components/Salesanalytics";
import Operationsanalytics from "./components/Operationsanalytics";
import Competitor from "./components/Competitor";
import CustomerView from "./components/CustomerView";
import SpecificCustomer from "./components/SpecificCustomers";
import PricingAnalysis from "./components/PricingAnalytics";
import Profile1 from "./components/Profile1";
import Projection from "./components/Projection";
import SalesPart from "./components/SalesPart";
import TenderAwarded from "./pages/TendersAwarded";
import TendersAwarded2 from "./pages/TendersAwarded2";
import CsvUpload from "./pages/CsvUpload";
import AwardedTenders from "./pages/AwardedTenders";
/* -------------------- AXIOS INTERCEPTOR -------------------- */
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const message = error.response?.data?.message;

    const AUTH_MESSAGES = [
      "Session expired",
      "Unauthorized",
      "Authorization header missing",
      "Invalid authorization format",
      "Invalid token",
    ];

    if (status === 401 && AUTH_MESSAGES.includes(message)) {
      localStorage.removeItem("token");
      localStorage.removeItem("user");

      if (!window.location.pathname.startsWith("/login")) {
        window.location.href = "/login";
      }
    }

    return Promise.reject(error);
  }
);
/* -------------------- END AXIOS INTERCEPTOR -------------------- */
const Terms = React.lazy(() => import("./pages/Terms"));
const Privacy = React.lazy(() => import("./pages/Privacy"));
const RefundCancellationPolicy = React.lazy(() => import("./pages/RefundCancellationPolicy"));
const TenderDetails = React.lazy(() => import("./pages/TenderDetails"));
const ContactUs = React.lazy(() => import("./pages/ContactUs"));
const MyTenders = React.lazy(() => import("./pages/MyTenders"));
const ArchivedTender = React.lazy(() => import("./pages/ArchivedTenders"));
const Services = React.lazy(() => import("./pages/Services"));
const ResultTenderDetails = React.lazy(() =>
  import("./pages/ResultTenderDetails")
);
const SalesDashboard = React.lazy(() =>
  import("./pages/SalesDashboard")
);
const Subscription = React.lazy(() =>
  import("./pages/SubscriptionManagement")
);
const Profile = React.lazy(() => import("./pages/Profile"));
const UserManagement = React.lazy(() => import("./pages/UserManagement"));
const CompetitorList = React.lazy(() =>
  import("./pages/CompetitorList")
);
const AllOrganizations = React.lazy(() =>
  import("./pages/AllOrganizations")
);
const AllRelatedTenders = React.lazy(() =>
  import("./pages/AllRelatedTenders")
);
const AllStates = React.lazy(() => import("./pages/AllStates"));
const AllMinistries = React.lazy(() =>
  import("./pages/AllMinistries")
);
const Results = React.lazy(() => import("./pages/Results"));
const ActiveTenders = React.lazy(() =>
  import("./pages/ActiveTenders")
);
const BidChart = React.lazy(() => import("./pages/BidChart"));
const IndustryTrends = React.lazy(() =>
  import("./pages/IndustryTrends")
);
const ReverseAuctionTenders = React.lazy(() =>
  import("./pages/ReverseAuctionTenders")
);

/* -------------------- BLOG LAZY IMPORTS -------------------- */
const BlogsPage = React.lazy(() =>
  import("./pages/BlogsPage")
);
const BlogDetailPage = React.lazy(() =>
  import("./pages/BlogDetailPage")
);
const AddBlog = React.lazy(() =>
  import("./pages/AddBlog")
);
const UpdateBlog = React.lazy(() =>
  import("./pages/UpdateBlog")
);
const AdminBlogsList = React.lazy(() =>
  import("./pages/AdminBlogsList")
);

/* -------------------- ROUTES -------------------- */
const AppRoutes = ({
  tenders,
  onToggleFavourite,
  showAuthModal,
  setShowAuthModal,
}) => {
  const location = useLocation();

  useEffect(() => {
    setShowAuthModal(false);
  }, [location.pathname]);

  const openAuthModal = () => setShowAuthModal(true);

  /* ---------- WRAPPERS ---------- */

  const withLayout = (component) => (
    <Layout openAuthModal={openAuthModal}>{component}</Layout>
  );

  const withSuspense = (component) => (
    <Suspense fallback={<PageSkeleton />}>{component}</Suspense>
  );

  const withProtectedLayout = (component, role) => (
    <ProtectedRoute allowedRole={role}>
      {withLayout(withSuspense(component))}
    </ProtectedRoute>
  );

  const withPublicLayout = (component) =>
    withLayout(withSuspense(component));

  /* ---------- ROUTES ---------- */

  const routes = useRoutes([
 {
  path: "/",
  element: withPublicLayout (
    <Dashboard
      showAuthModal={showAuthModal}
      setShowAuthModal={setShowAuthModal}
    />
  )
},
    {
      path: "/checklist/:id",
      element: withProtectedLayout(<CheckList />)
    },
    {
      path: "/terms",
      element: withPublicLayout(<Terms />)
    },
    {
      path: "/privacy",
      element: withPublicLayout(<Privacy />)
    },
    {
      path: "/refund-policy",
      element: withPublicLayout(<RefundCancellationPolicy />)
    },
    {
      path: "/document-checklist/:id",
      element: withProtectedLayout(<DocumentChecklist />)
    },
   {
 path: "/Tender-Awarded/:tenderId",
  element: withProtectedLayout(<TenderAwarded />)
},
    {
      path: "/Tender-Awarded2/:tenderId",
      element: withProtectedLayout(<TendersAwarded2 />)
    },
    {
      path: "/dashboard",
      element: withPublicLayout(
        <Dashboard
          showAuthModal={showAuthModal}
          setShowAuthModal={setShowAuthModal}
        />
      ),
    },
    {
      path: "/login",
      element: withSuspense(<Login />)
    },
    {
      path: "/signup",
      element: withSuspense(<Signup />)
    },
    {
      path: "/forgot-password",
      element: withSuspense(<ForgotPassword />)
    },
    {
      path: "/emp-customer-assignment",
      element: withPublicLayout(<Empcustomerassi />),
    },
    {
      path: "/upload-csv",
      element: withProtectedLayout(<CsvUpload />, ["customer", "operations"])
    },
    {
      path: "/billing-table",
      element: withProtectedLayout(
        <BillingTable
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/activeTenders",
      element: withPublicLayout(
        <ActiveTenders
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
          openAuthModal={openAuthModal}
        />
      ),
    },
    {
  path: "/awarded-tenders",
  element: withPublicLayout(
    <AwardedTenders
      tenders={tenders}
      onToggleFavourite={onToggleFavourite}
      openAuthModal={openAuthModal}
    />
  ),
},
    {
      path: "/TenderList",
      element: withProtectedLayout(
        <TenderList
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/Report",
      element: withProtectedLayout(
        <Report
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/FinanceReport",
      element: withProtectedLayout(
        <FinanceReport
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/OperationsReport",
      element: withProtectedLayout(
        <OperationsReport
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/user-management",
      element: withProtectedLayout(<UserManagement />, "admin"),
    },
    {
      path: "/TaskList",
      element: withProtectedLayout(
        <TaskList
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/FeedBack",
      element: withProtectedLayout(
        <FeedBack
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/FeedBackSales",
      element: withProtectedLayout(
        <FeedBackSales
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/tender/:id",
      element: withProtectedLayout(
        <TenderDetails
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/results",
      element: withProtectedLayout(
        <Results
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/results/:id",
      element: withProtectedLayout(
        <ResultTenderDetails
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/tenders/:id/bidders",
      element: withProtectedLayout(
        <TenderBiddersPage />
      ),
    },
    {
      path: "/ArchivedTenders",
      element: withPublicLayout(
        <ArchivedTender
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
          openAuthModal={openAuthModal}
        />
      ),
    },
    {
      path: "/reverse-auction-tenders",
      element: withPublicLayout(
        <ReverseAuctionTenders
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
          openAuthModal={openAuthModal}
        />
      ),
    },
    {
      path: "/mytender",
      element: withProtectedLayout(
        <MyTenders
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/services",
      element: withPublicLayout(
        <Services
          tenders={tenders}
          onToggleFavourite={onToggleFavourite}
        />
      ),
    },
    {
      path: "/contactUs",
      element: withPublicLayout(<ContactUs />),
    },
    /* -------------------- BLOG ROUTES -------------------- */
    {
      path: "/blogs",
      element: withPublicLayout(<BlogsPage />),
    },
    {
      path: "/blogs/:id",
      element: withPublicLayout(<BlogDetailPage />),
    },
    {
      path: "/admin-blogs",
      element: withProtectedLayout(
        <AdminBlogsList />,
        ["admin", "owner"]
      ),
    },
    {
      path: "/admin-blogs/add",
      element: withProtectedLayout(
        <AddBlog />,
        ["admin", "owner"]
      ),
    },
    {
      path: "/admin-blogs/update/:id",
      element: withProtectedLayout(
        <UpdateBlog />,
        ["admin", "owner"]
      ),
    },
    /* -------------------- END BLOG ROUTES -------------------- */
    {
      path: "/profile",
      element: withProtectedLayout(<Profile />),
    },
    {
      path: "/subscription",
      element: withProtectedLayout(<Subscription />),
    },
    {
      path: "/related-tenders",
      element: withProtectedLayout(<AllRelatedTenders />),
    },
    {
      path: "/organizations",
      element: withProtectedLayout(<AllOrganizations />),
    },
    {
      path: "/ministries",
      element: withProtectedLayout(<AllMinistries />),
    },
    {
      path: "/states",
      element: withProtectedLayout(<AllStates />),
    },
    {
      path: "/mailer-list",
      element: withProtectedLayout(<MailerList />, ["admin", "sales"]),
    },
    {
      path: "/sales-dashboard",
      element: withProtectedLayout(
        <SalesDashboard />,
        ["sales", "admin"]
      ),
    },
    {
      path: "/analytics/competitors",
      element: withProtectedLayout(
        <CompetitorList />,
        "owner"
      ),
    },
    {
      path: "/analytics/bid-chart",
      element: withProtectedLayout(<BidChart />, "owner"),
    },
    {
      path: "/analytics/trends",
      element: withProtectedLayout(
        <IndustryTrends />,
        "owner"
      ),
    },
    {
      path: "/admin-analytics",
      element: withProtectedLayout(<AdminAnalytics />),
    },
    {
      path: "/sales-analytics",
      element: withProtectedLayout(<Salesanalytics />, "sales"),
    },
    {
      path: "/operations-analytics",
      element: withProtectedLayout(<Operationsanalytics />, "operations"),
    },
    {
      path: "/customer-analytics",
      element: withProtectedLayout(<Customeranalytics />),
    },
    {
      path: "/finance-analytics",
      element: withProtectedLayout(<Financeanalytics />, ["finance"]),
    },
    {
      path: "/competitor",
      element: withProtectedLayout(<Competitor />),
    },
    {
      path: "/CustomerView",
      element: withProtectedLayout(<CustomerView />),
    },
    {
      path: "/SpecificCustomers",
      element: withProtectedLayout(<SpecificCustomer />),
    },
    {
      path: "/PricingAnalysis",
      element: withProtectedLayout(<PricingAnalysis />),
    },
    {
      path: "/Profile1",
      element: withProtectedLayout(<Profile1 />),
    },
    {
      path: "/Projection",
      element: withProtectedLayout(<Projection />),
    },
    {
      path: "/SalesPart",
      element: withProtectedLayout(<SalesPart />),
    },
    {
      path: "*",
      element: <Navigate to="/" replace />
    },
  ]);

  return routes;
};

/* -------------------- APP -------------------- */
const App = () => {
  const [tenders, setTenders] = useState(TENDERS);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const toggleFavourite = (id) => {
    setTenders((prev) =>
      prev.map((t) =>
        t.id === id
          ? { ...t, isFavourite: !t.isFavourite }
          : t
      )
    );
  };

  return (
    <AuthProvider>
      <BrowserRouter>
        <ToastContainer
          position="bottom-right"
          autoClose={2000}
          hideProgressBar
          newestOnTop
          closeOnClick
          pauseOnHover
          theme="light"
        />

        <AppRoutes
          tenders={tenders}
          onToggleFavourite={toggleFavourite}
          showAuthModal={showAuthModal}
          setShowAuthModal={setShowAuthModal}
        />

        <AuthModal
          show={showAuthModal}
          onHide={() => setShowAuthModal(false)}
        />
      </BrowserRouter>
    </AuthProvider>
  );
};

export default App;